// Mục Kho (kho-erp v1, CEO duyệt bản mẫu 01/10/2026). Kho GNW là sự thật duy
// nhất; GNPF chỉ đọc bản sao và gửi lệnh — máy chủ cắt dữ liệu theo vai.
import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { serverErrorMessage } from "@/lib/api/error-message";
import type { components } from "@/lib/api/generated/schema";
import { ACTION } from "@/lib/auth/actions";
import { hasPermission } from "@/lib/authv2/permissions";
import { useKhoFlag } from "./flag";

export { useKhoFlag };

export type KhoConfig = components["schemas"]["KhoConfig"];
export type KhoOverview = components["schemas"]["KhoOverview"];
export type KhoAsset = components["schemas"]["KhoAsset"];
export type KhoAway = components["schemas"]["KhoAway"];
export type KhoTask = components["schemas"]["KhoTask"];
export type KhoCommand = components["schemas"]["KhoCommand"];
export type KhoAssetDetail = components["schemas"]["KhoAssetDetail"];

export type KhoAction =
  | "task_create" | "task_cancel" | "task_claim" | "task_start" | "task_done"
  | "loan_extend_request" | "asset_report_damage" | "asset_report_lost"
  | "external_borrow_open" | "external_borrow_close" | "customer_rental_open" | "customer_rental_close";

export const khoKeys = {
  config: ["kho", "config"] as const,
  overview: (branch: string) => ["kho", "overview", branch] as const,
  all: ["kho"] as const,
};

export function useKhoConfig(enabled = true) {
  return useQuery({
    queryKey: khoKeys.config,
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/kho/config");
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được cấu hình mục Kho"));
      return data;
    },
  });
}

/** Nạp công tắc mục Kho (và máy quét mượn/trả) cho menu. */
export function useKhoConfigSync(loggedIn: boolean) {
  const allowed = loggedIn && hasPermission(ACTION.KhoRead);
  const q = useKhoConfig(allowed);
  const setEnabled = useKhoFlag((s) => s.setEnabled);
  const on = allowed && !!q.data?.enabled;
  useEffect(() => setEnabled(on), [on, setEnabled]);
  const scannerQ = useQuery({
    queryKey: ["kho", "scanner"],
    enabled: on,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/kho/scanner");
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được công tắc máy quét"));
      return data;
    },
  });
  const setScanner = useKhoFlag((s) => s.setScanner);
  const scannerOn = on && !!scannerQ.data?.enabled;
  useEffect(() => setScanner(scannerOn), [scannerOn, setScanner]);
}

export function useKhoOverview(branch: string, enabled = true) {
  return useQuery({
    queryKey: khoKeys.overview(branch),
    enabled,
    queryFn: async () => {
      const { data, error } = await api.GET("/kho/overview", {
        params: { query: branch ? { branch } : {} },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được mục Kho"));
      return data;
    },
    // Lệnh còn chờ kho trả lời thì hỏi lại dày để nút mở khoá sớm.
    // Lệnh đã kẹt (quá 30 phút) thì thôi hỏi dày — người dùng tự Gửi lại / Bỏ.
    refetchInterval: (q) => (q.state.data?.commands.some((c) => c.state === "queued" && !c.stuck) ? 5_000 : 60_000),
  });
}

export function useKhoCommand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { action: KhoAction; payload: Record<string, unknown> }) => {
      const { data, error } = await api.POST("/kho/commands", { body: input });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không gửi được yêu cầu sang kho"));
      return data;
    },
    onSettled: () => qc.invalidateQueries({ queryKey: khoKeys.all }),
  });
}

/** Lệnh kẹt quá 30 phút: gửi lại nguyên byte / bỏ tại chỗ. */
export function useKhoStuckAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { eventId: string; op: "resend" | "dismiss" }) => {
      const params = { path: { eventId: input.eventId } };
      const { data, error } =
        input.op === "resend"
          ? await api.POST("/kho/commands/{eventId}/resend", { params })
          : await api.POST("/kho/commands/{eventId}/dismiss", { params });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không thực hiện được"));
      return data;
    },
    onSettled: () => qc.invalidateQueries({ queryKey: khoKeys.all }),
  });
}

/** Chi tiết một món (kho-erp v1.7): ảnh, hãng, số máy, ghi chú — chỉ tải khi mở ngăn. */
export function useKhoAssetDetail(assetId: string | null) {
  return useQuery({
    queryKey: ["kho", "asset", assetId] as const,
    enabled: !!assetId,
    queryFn: async () => {
      const { data, error } = await api.GET("/kho/assets/{assetId}", { params: { path: { assetId: assetId! } } });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được chi tiết món"));
      return data;
    },
  });
}

// ── Gắn đồ với concept / set + thiếu đồ (kho-erp v1.8, CEO 1c…10a 04/11/2026) ──
// Kho là nơi lưu DUY NHẤT; GNPF gửi lệnh, Kho áp rồi gửi lại món / định mức.

export type KhoKitAction = "kit_line_upsert" | "kit_line_remove" | "asset_concepts_set" | "asset_fix_to_set" | "asset_unfix";
export type KhoConceptRef = components["schemas"]["KhoConceptRef"];
export type KhoKitItem = components["schemas"]["KhoKitItem"];
export type KhoConceptNeed = components["schemas"]["KhoConceptNeed"];
export type KhoShortageRow = components["schemas"]["KhoShortageRow"];
export type KhoKitLine = components["schemas"]["KhoKitLine"];

/** Công tắc phần gắn đồ + người xem có sửa định mức được không. */
export function useKhoKitConfig(enabled = true) {
  return useQuery({
    queryKey: ["kho", "kit", "config"] as const,
    enabled: enabled && hasPermission(ACTION.KhoRead),
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/kho/kit/config");
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được công tắc gắn đồ"));
      return data;
    },
  });
}

/** Concept đang bán + set đang mở của một cơ sở, và người xem gắn được không. */
export function useKhoKitOptions(branch: string | null, enabled = true) {
  return useQuery({
    queryKey: ["kho", "kit", "options", branch] as const,
    enabled: enabled && !!branch,
    queryFn: async () => {
      const { data, error } = await api.GET("/kho/kit/options", { params: { query: { branch: branch! } } });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được danh sách concept / set"));
      return data;
    },
  });
}

export function useKhoKitCommand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { action: KhoKitAction; payload: Record<string, unknown> }) => {
      const { data, error } = await api.POST("/kho/kit/commands", { body: input });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không gửi được sang Kho"));
      return data;
    },
    onSettled: () => qc.invalidateQueries({ queryKey: khoKeys.all }),
  });
}

/** Thẻ Thiếu đồ: 3 ngày tới của một cơ sở. */
export function useKhoShortages(branch: string | null, enabled = true) {
  return useQuery({
    queryKey: ["kho", "kit", "shortages", branch] as const,
    enabled: enabled && !!branch,
    queryFn: async () => {
      const { data, error } = await api.GET("/kho/kit/shortages", { params: { query: { branch: branch! } } });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được danh sách thiếu đồ"));
      return data;
    },
  });
}

/** Bảng set: đồ dựng ở set + đồ từng concept của set cần. */
export function useKhoSetKit(branch: string, floor: number, set: number, enabled = true) {
  return useQuery({
    queryKey: ["kho", "kit", "set", branch, floor, set] as const,
    enabled,
    queryFn: async () => {
      const { data, error } = await api.GET("/kho/kit/sets", {
        params: { query: { branch, floor, set } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được đồ của set"));
      return data;
    },
  });
}

/** Concept & Trang phục — thẻ Đồ đạc của một concept. */
export function useKhoConceptKit(conceptId: string | null) {
  return useQuery({
    queryKey: ["kho", "kit", "concept", conceptId] as const,
    enabled: !!conceptId,
    queryFn: async () => {
      const { data, error } = await api.GET("/kho/kit/concepts/{conceptId}", { params: { path: { conceptId: conceptId! } } });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được đồ đạc của concept"));
      return data;
    },
  });
}
